import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import AuthPage from './pages/AuthPage';
import ResetPassword from './pages/ResetPassword';
import ConfirmInvite from './pages/ConfirmInvite';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import SiteWalk from './pages/SiteWalk';
import Messages from './pages/Messages';
import ClientDashboard from './pages/ClientDashboard';
import Chat from './pages/Chat';
import TimeClock from './pages/TimeClock';
import Profile from './pages/Profile';
import Team from './pages/Team';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import { VideoSettings } from './pages/VideoSettings';
import PendingApproval from './pages/PendingApproval';
import WorkSchedule from './pages/WorkSchedule';
import NotesManagement from './pages/NotesManagement';
import SubContractorDashboard from './pages/SubContractorDashboard';
import SubContractors from './pages/SubContractors';
import WeeklyHours from './pages/WeeklyHours';
import PhotoAlbums from './pages/PhotoAlbums';
import AlbumDetail from './pages/AlbumDetail';
import AlbumShare from './pages/AlbumShare';
import AdminPhotoAlbums from './pages/AdminPhotoAlbums';
import Calendar from './pages/Calendar';
import MySchedule from './pages/MySchedule';
import Tools from './pages/Tools';
import MyTools from './pages/MyTools';
import MyPersonalTools from './pages/MyPersonalTools';
import PendingPhotos from './pages/PendingPhotos';
import EstimatorTools from './pages/EstimatorTools';
import JobMap from './pages/JobMap';
import Leaderboard from './pages/Leaderboard';
import HoursAnalytics from './pages/HoursAnalytics';
import MyAnalytics from './pages/MyAnalytics';
import Connections from './pages/Connections';
import MessageLog from './pages/MessageLog';
import SplashScreen from './components/SplashScreen';
import WelcomeSplash from './components/WelcomeSplash';
import BadgeCelebration from './components/BadgeCelebration';
import Navigation from './components/Navigation';
import StickyHeader from './components/StickyHeader';
import UpdateNotification from './components/UpdateNotification';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { ClientChatbot } from './components/ClientChatbot';
import { PullToRefresh } from './components/PullToRefresh';
import { Toaster } from './components/ui/sonner';
import { UnreadNotesContext } from './contexts/UnreadNotesContext';
import { useActivityTracking } from './hooks/useActivityTracking';
import { useBadgeNotifications } from './hooks/useBadgeNotifications';
import { PermissionsProvider } from './hooks/usePermissions';

import { getBackendUrl } from './utils/getBackendUrl';
import { OfflineBanner } from './components/OfflineBanner';
import { initOfflineSync } from './utils/offlineQueue';
import { updateAppBadge } from './utils/pushNotifications';
import { loadSavedTheme, applyTheme } from './utils/theme';
import Appearance from './pages/Appearance';
import Notifications from './pages/Notifications';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Add axios response interceptor to handle all error responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log the error for debugging
    console.error('Axios error:', error.response?.status, error.response?.data);
    // Always reject on error so it goes to catch blocks
    return Promise.reject(error);
  }
);

// Create Site Settings Context
export const SiteSettingsContext = createContext();

export const useSiteSettings = () => useContext(SiteSettingsContext);

// Wrapper component that uses the activity tracking hook inside Router context
function AppContent({ user, handleLogin, handleLogout, siteSettings, fetchSiteSettings, refreshUnreadCount, setRefreshUnreadCount, fetchUser }) {
  // Track user activity for online status (must be inside Router)
  useActivityTracking(user);
  
  // Update PWA badge notifications
  useBadgeNotifications(user);

  // Preserve deep links through login (e.g. email approval links)
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user && location.pathname !== '/' && location.pathname !== '/reset-password' && location.pathname !== '/confirm-invite') {
      sessionStorage.setItem('postLoginRedirect', location.pathname + location.search);
    }
  }, [user, location]);
  useEffect(() => {
    if (user) {
      const dest = sessionStorage.getItem('postLoginRedirect');
      if (dest) {
        sessionStorage.removeItem('postLoginRedirect');
        navigate(dest, { replace: true });
      }
    }
  }, [user]);

  const handleRefresh = async () => {
    // Refresh user data and site settings
    if (user) {
      await fetchUser();
      await fetchSiteSettings();
    }
    return Promise.resolve();
  };

  return (
    <>
      {/* Offline status + sync banner */}
      {user && <OfflineBanner />}
      {/* Update notification banner */}
      <UpdateNotification />
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      <PullToRefresh onRefresh={handleRefresh} />
      
      {/* Render header and navigation once for all authenticated routes */}
      {user && <StickyHeader key={user.id} user={user} />}
      <Routes>
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={
                (user.role === 'admin' || user.role === 'supervisor') ? '/dashboard' : 
                user.role === 'client' ? '/client-dashboard' : 
                (user.role === 'employee' || user.role === 'sub_contractor') ? '/dashboard' : 
                '/profile'
              } />
            ) : (
              <AuthPage onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        <Route
          path="/confirm-invite"
          element={<ConfirmInvite />}
        />
        <Route
          path="/dashboard"
          element={
            user ? (
              user.role === 'sub_contractor' ? (
                <SubContractorDashboard user={user} />
              ) : user.role === 'client' ? (
                <Navigate to="/client-dashboard" />
              ) : (
                <Dashboard user={user} />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/client-dashboard"
          element={
            user ? (
              user.role === 'client' ? (
                <ClientDashboard user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/client-dashboard/:clientId"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'supervisor') ? (
                <ClientDashboard user={user} />
              ) : (
                <Navigate to="/client-dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/sub-dashboard/:userId"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'supervisor') || user.role === 'sub_contractor' ? (
                <SubContractorDashboard user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/projects"
          element={user ? <Projects user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/projects/:projectId"
          element={user ? <ProjectDetail user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/appearance"
          element={user ? <Appearance /> : <Navigate to="/" />}
        />
        <Route
          path="/notifications"
          element={user ? <Notifications /> : <Navigate to="/" />}
        />
        <Route
          path="/weekly-hours"
          element={user ? <WeeklyHours user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/services"
          element={user ? <Services user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/services/:serviceId"
          element={user ? <ServiceDetail user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/chat"
          element={user ? <Chat user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/timeclock"
          element={user ? <TimeClock user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/notes-management"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'supervisor') ? (
                <NotesManagement user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/gallery"
          element={<Navigate to="/projects" replace />}
        />
        <Route
          path="/profile"
          element={user ? <Profile user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
        <Route
          path="/profile/:userId"
          element={user ? <Profile user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
        />
        <Route
          path="/team"
          element={user ? <Team user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/subcontractors"
          element={
            user ? (
              (user.role === 'admin' || user.role === 'supervisor') ? (
                <SubContractors user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/tools"
          element={
            user ? (
              (user.role === 'admin' || user.role === 'supervisor') ? (
                <Tools user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/my-tools"
          element={
            user ? (
              user.role === 'employee' ? (
                <MyTools user={user} />
              ) : (user.role === 'admin' || user.role === 'supervisor') ? (
                <MyTools user={user} />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/estimator"
          element={user && user.role !== 'client' ? <EstimatorTools /> : <Navigate to="/" />}
        />
        <Route
          path="/job-map"
          element={user && user.role !== 'client' ? <JobMap user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/leaderboard"
          element={user && user.role !== 'client' ? <Leaderboard user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/photos/pending"
          element={user && (user.role === 'admin' || user.role === 'supervisor') ? <PendingPhotos /> : <Navigate to="/" />}
        />
        <Route
          path="/my-personal-tools"
          element={user && user.role !== 'client' ? <MyPersonalTools user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/site-walk"
          element={user ? <SiteWalk user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/messages"
          element={user ? <Messages user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/reports"
          element={user ? <Reports user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/settings"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'supervisor') ? (
                <Settings user={user} />
              ) : (
                <Navigate to="/profile" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/video-settings"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor') || user.role === 'supervisor') ? (
                <VideoSettings user={user} />
              ) : (
                <Navigate to="/" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/pending-approval"
          element={<PendingApproval />}
        />
        <Route
          path="/work-schedule"
          element={
            user && (user.role === 'employee' || user.role === 'sub_contractor') ? (
              <WorkSchedule user={user} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/photo-albums"
          element={user ? <PhotoAlbums user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/photo-albums/:albumId"
          element={user ? <AlbumDetail user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/photo-albums/:albumId/share"
          element={user ? <AlbumShare user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/admin/photo-albums"
          element={
            user ? (
              ((user.role === 'admin' || user.role === 'supervisor')) ? (
                <AdminPhotoAlbums user={user} />
              ) : (
                <Navigate to="/photo-albums" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/calendar"
          element={
            user ? (
              (user.role === 'admin' || user.role === 'supervisor') ? (
                <Calendar user={user} />
              ) : (
                <Navigate to="/my-schedule" />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/my-schedule"
          element={user ? <MySchedule user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/hours-analytics"
          element={user && (user.role === 'admin' || user.role === 'supervisor') ? <HoursAnalytics /> : <Navigate to="/" />}
        />
        <Route
          path="/my-analytics"
          element={user && user.role !== 'client' ? <MyAnalytics /> : <Navigate to="/" />}
        />
        <Route
          path="/connections"
          element={user && (user.role === 'admin' || user.role === 'employee') ? <Connections user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/message-log"
          element={user && user.role !== 'client' ? <MessageLog user={user} /> : <Navigate to="/" />}
        />
      </Routes>
      {user && <Navigation user={user} onLogout={handleLogout} />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [welcomeUser, setWelcomeUser] = useState(null);
  const [siteSettings, setSiteSettings] = useState({
    site_title: 'BuildTrack',
    site_logo: null
  });
  const [refreshUnreadCount, setRefreshUnreadCount] = useState(0);

  useEffect(() => {
    initOfflineSync();
    loadSavedTheme();
  }, []);

  // Auto-reload when a new build is deployed (checks on app open/resume + every 30 min)
  useEffect(() => {
    const current = document.querySelector('script[src*="/static/js/main"]')?.getAttribute('src');
    if (!current) return undefined; // dev mode
    let reloaded = false;
    const check = async () => {
      try {
        const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
        const html = await res.text();
        const m = html.match(/static\/js\/main\.[^"']+\.js/);
        if (m && !current.includes(m[0]) && !reloaded) {
          reloaded = true;
          window.location.reload();
        }
      } catch (e) { /* offline — skip */ }
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    const iv = setInterval(check, 30 * 60 * 1000);
    return () => { document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/theme-prefs`).then((r) => {
      if (r.data?.preset) applyTheme(r.data);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    updateAppBadge();
    const onVisible = () => {
      if (document.visibilityState === 'visible') updateAppBadge();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(updateAppBadge, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [user]);

  useEffect(() => {
    fetchSiteSettings();
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/site`);
      setSiteSettings(response.data);
      // Update document title
      document.title = `${response.data.site_title} | Construction Project Management`;
      
      // Update apple-mobile-web-app-title meta tag
      let appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleTitleMeta) {
        appleTitleMeta.setAttribute('content', response.data.site_title);
      }
      
      // Update Open Graph meta tags for social sharing
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', response.data.site_title);
      }
      
      let ogSiteName = document.querySelector('meta[property="og:site_name"]');
      if (ogSiteName) {
        ogSiteName.setAttribute('content', response.data.site_title);
      }
      
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', response.data.site_title);
      }
      
      // Apply background color
      if (response.data.background_color) {
        document.body.style.backgroundColor = response.data.background_color;
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    if (userData.role === 'client' || userData.role === 'employee') {
      setWelcomeUser(userData);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Show splash screen first, then loading spinner, then main app
  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <SiteSettingsContext.Provider value={{ siteSettings, fetchSiteSettings }}>
      <UnreadNotesContext.Provider value={{ refreshUnreadCount, triggerRefresh: () => setRefreshUnreadCount(prev => prev + 1) }}>
        <PermissionsProvider>
          <div className="App">
            {welcomeUser && <WelcomeSplash user={welcomeUser} onDone={() => setWelcomeUser(null)} />}
            {user && <BadgeCelebration user={user} />}
            <BrowserRouter>
              <AppContent 
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                siteSettings={siteSettings}
                fetchSiteSettings={fetchSiteSettings}
                refreshUnreadCount={refreshUnreadCount}
                setRefreshUnreadCount={setRefreshUnreadCount}
                fetchUser={fetchUser}
              />
            </BrowserRouter>
        <Toaster position="top-center" />
      </div>
        </PermissionsProvider>
    </UnreadNotesContext.Provider>
    </SiteSettingsContext.Provider>
  );
}

export default App;